'use client';

import { getProduct, products } from '@/lib/products';
import { BrowserShot, Tilt } from './shots';
import { Reveal } from './Reveal';
import { ProductHero } from './hero';
import {
  AccentGlow,
  DemoBand,
  FeatureRows,
  FloatChip,
  GalleryRail,
  NextProducts,
  ProductMarquee,
  SectionHead,
  StatGrid,
} from './blocks';

const product = getProduct('dilaerp')!;
const A = product.accent;
const IMG = '/images/products/dilaerp';

const MODULES = [
  'Sales',
  'Purchases',
  'Inventory',
  'Accounting',
  'HR & Payroll',
  'Point of Sale',
  'Projects',
  'Support',
  'CRM',
  'Assets',
  'Reports',
  'Healthcare',
  'LMS',
  'Helpdesk',
  'Stock analytics',
];

export function DilaErpPage() {
  return (
    <div className="self-styled relative text-white">
      {/* ── Hero ─────────────────────────────────────────────────────── */}
      <ProductHero
        name="DilaERP"
        accent={A}
        title="the erp that speaks kurdish"
        lede="Sales, stock, accounting, HR, payroll and POS in one simple system. Fifteen modules, three languages, your own server."
        secondary={{ label: 'Open live demo', href: product.liveUrl, external: true }}
        liveHref={product.liveUrl}
        liveLabel={`${product.liveLabel} · ${product.domain}`}
        note="Request demo access"
      >
        <AccentGlow accent={A} className="left-1/4 top-1/4 h-80 w-80" opacity={0.18} />
        <Tilt>
          <BrowserShot
            src={`${IMG}/dashboard.webp`}
            alt={product.imageAlt}
            url={product.domain}
            accent={A}
            aspect="aspect-[16/10]"
            priority
            sizes="(max-width: 1024px) 100vw, 55vw"
          />
        </Tilt>
        <FloatChip accent={A} className="-left-4 top-10" dot>
          Profit &amp; Loss
        </FloatChip>
        <FloatChip accent={A} className="-right-3 top-1/3" alt>
          Payroll
        </FloatChip>
        <FloatChip accent={A} className="bottom-8 left-1/4">
          POS · Stock · CRM
        </FloatChip>
      </ProductHero>

      {/* ── The one marquee: product truths ──────────────────────────── */}
      <ProductMarquee
        accent={A}
        items={[
          'Self-hosted',
          'Your data stays yours',
          'Sorani PDFs',
          '71 print formats',
          '26+ reports',
          'AI assistant',
          'Simple mode',
          'Multi-company',
          'کوردی',
          'العربية',
          'English',
        ]}
      />

      {/* ── Module constellation ─────────────────────────────────────── */}
      <section aria-label="Modules" className="px-6 py-24 md:px-12 md:py-32 lg:px-24">
        <div className="mx-auto max-w-[1400px]">
          <Reveal>
            <SectionHead
              accent={A}
              title="fifteen modules. one login."
              lede="Everything an ERP needs, curated down to one clean screen per job. No dense legacy tables, no module marketplace roulette."
              className="max-w-4xl"
            />
          </Reveal>
          <Reveal delay={120}>
            <ul className="mt-14 flex flex-wrap items-center gap-3 md:gap-4" aria-label="Module list">
              {MODULES.map((mod, i) => (
                <li
                  key={mod}
                  className={
                    i % 4 === 0
                      ? 'rounded-full border px-6 py-3 font-mono text-xs uppercase tracking-[0.22em] text-white md:text-sm'
                      : 'rounded-full border border-white/12 px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-white/55'
                  }
                  style={i % 4 === 0 ? { borderColor: `${A}55`, color: A } : undefined}
                >
                  {mod}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* ── AI assistant (split: copy left, shot right) ──────────────── */}
      <section aria-label="AI assistant" className="border-t border-white/10 px-6 py-24 md:px-12 md:py-32 lg:px-24">
        <div className="mx-auto grid max-w-[1400px] items-center gap-14 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <SectionHead
              accent={A}
              title="ask it in kurdish"
              lede="“داهاتمان لەم مانگەدا چەندە؟” — our revenue this month, answered from live data, in the language you asked in. About fifty-four ERP tools sit behind the chat."
            />
            <FeatureRows
              accent={A}
              className="mt-10"
              rows={[
                {
                  title: 'Writes pass an approval card',
                  body: 'Create the invoice, record the payment, but only after you confirm the card. Nothing writes itself.',
                },
                {
                  title: 'Your permissions, not a backdoor',
                  body: 'The assistant runs on your session, so a cashier can never ask it for payroll.',
                },
              ]}
            />
          </Reveal>
          <Reveal className="relative lg:col-span-7" delay={120}>
            <AccentGlow accent={A} className="-right-8 bottom-0 h-64 w-64" opacity={0.15} />
            <BrowserShot
              src={`${IMG}/ai-ku.webp`}
              alt="DilaERP AI assistant answering a revenue question written in Kurdish, with the right-to-left sidebar behind."
              url={product.domain}
              accent={A}
              sizes="(max-width: 1024px) 100vw, 55vw"
            />
          </Reveal>
        </div>
      </section>

      {/* ── Simple Mode / POS (split: shot left, copy right) ─────────── */}
      <section aria-label="Simple mode and POS" className="border-t border-white/10 px-6 py-24 md:px-12 md:py-32 lg:px-24">
        <div className="mx-auto grid max-w-[1400px] items-center gap-14 lg:grid-cols-12">
          <Reveal className="relative order-2 lg:order-1 lg:col-span-7" delay={120}>
            <AccentGlow accent={A} className="-left-8 top-10 h-64 w-64" opacity={0.15} />
            <BrowserShot
              src={`${IMG}/simple-sell.webp`}
              alt="DilaERP Simple Mode sell screen on mobile with product grid, cart and large checkout button."
              url={`${product.domain}/simple`}
              accent={A}
              aspect="aspect-[4/3]"
              sizes="(max-width: 1024px) 100vw, 55vw"
            />
          </Reveal>
          <Reveal className="order-1 lg:order-2 lg:col-span-5">
            <SectionHead
              accent={A}
              title="head office power, shop-floor simple"
              lede="Simple Mode distills the whole ERP to Sell, Stock, Money and More on any phone. Barcode POS, 80mm thermal receipts, shift sessions, tolerant of bad internet."
            />
            <FeatureRows
              accent={A}
              className="mt-10"
              rows={[
                {
                  title: 'A cashier learns it in an hour',
                  body: 'Big buttons, barcode search, tender modal with your payment modes. No ERP training manual required.',
                },
                {
                  title: 'Same books underneath',
                  body: 'Every sale posts to the same ledger the accountant sees. Simple on top, serious below.',
                },
              ]}
            />
          </Reveal>
        </div>
      </section>

      {/* ── Finance band ─────────────────────────────────────────────── */}
      <section aria-label="Finance" className="border-t border-white/10 px-6 py-24 md:px-12 md:py-32 lg:px-24">
        <div className="mx-auto max-w-[1400px]">
          <Reveal>
            <SectionHead
              accent={A}
              title="finance without fear"
              lede="Profit and loss, balance sheet, cash flow, trial balance and general ledger, plus twenty-six ready reports. Holding companies roll up into consolidated statements."
            />
          </Reveal>
          <Reveal delay={120} className="relative mt-14">
            <AccentGlow accent={A} className="right-1/4 -top-8 h-64 w-64" opacity={0.15} />
            <BrowserShot
              src={`${IMG}/pnl.webp`}
              alt="DilaERP profit and loss report with income, cost of goods sold and expenses in IQD."
              url={`${product.domain}/reports`}
              accent={A}
              aspect="aspect-[16/9]"
              sizes="(max-width: 1024px) 100vw, 85vw"
            />
          </Reveal>
        </div>
      </section>

      {/* ── Kurdish band (RTL display moment) ────────────────────────── */}
      <section aria-label="Kurdish first" className="border-t border-white/10 px-6 py-24 md:px-12 md:py-32 lg:px-24">
        <div className="mx-auto max-w-[1400px]">
          <Reveal>
            <span className="eyebrow" style={{ color: A }}>
              Kurdish first
            </span>
            <h2
              dir="rtl"
              lang="ckb"
              className="font-cairo mt-8 text-right text-5xl font-semibold leading-[1.15] text-white sm:text-6xl md:text-7xl"
            >
              یەک سیستەم بۆ تەواوی بزنسەکەت
            </h2>
            <p className="mt-6 max-w-2xl text-base font-light leading-relaxed text-white/65 md:text-lg">
              One system for your whole business. Interface, AI, PDFs and
              receipts in Sorani, Arabic and English, with right-to-left done
              properly, down to the digits on an invoice.
            </p>
          </Reveal>
          <Reveal delay={140} className="relative mt-14">
            <AccentGlow accent={A} className="left-1/4 -top-8 h-64 w-64" opacity={0.15} />
            <BrowserShot
              src={`${IMG}/dashboard-ku.webp`}
              alt="DilaERP dashboard fully in Kurdish Sorani with a right-to-left layout and localized KPI cards."
              url={product.domain}
              accent={A}
              aspect="aspect-[16/9]"
              sizes="(max-width: 1024px) 100vw, 85vw"
            />
          </Reveal>
        </div>
      </section>

      {/* ── Stats ────────────────────────────────────────────────────── */}
      <section aria-label="DilaERP in numbers" className="border-t border-white/10 px-6 py-24 md:px-12 md:py-28 lg:px-24">
        <div className="mx-auto max-w-[1400px]">
          <Reveal>
            <StatGrid
              accent={A}
              stats={[
                { value: '15', label: 'Business modules' },
                { value: '300', label: 'Screens' },
                { value: '6,392', label: 'Strings per language' },
                { value: '26+', label: 'Built-in reports' },
                { value: '~54', label: 'AI tools' },
                { value: '71', label: 'Print formats' },
              ]}
            />
          </Reveal>
        </div>
      </section>

      {/* ── Ownership + paperwork ────────────────────────────────────── */}
      <section aria-label="Ownership" className="border-t border-white/10 px-6 py-24 md:px-12 md:py-32 lg:px-24">
        <div className="mx-auto max-w-[1400px]">
          <Reveal>
            <SectionHead
              accent={A}
              title="self-hosted. your data stays yours."
              lede="Private-server deployments with masked APIs and zero upstream branding. Seventy-one branded print formats across six themes carry your letterhead, with Sorani glyphs embedded so Kurdish PDFs never show boxes."
              className="max-w-4xl"
            />
          </Reveal>
          <Reveal delay={120}>
            <FeatureRows
              accent={A}
              className="mt-14"
              rows={[
                {
                  title: 'Your server, your rules',
                  body: 'Run it on our cloud or your own machine. Either way the database is yours and exports are one click, always.',
                },
                {
                  title: 'Per-business toggles',
                  body: 'Modules, themes and features switch per tenant, so a pharmacy and a contractor never see the same clutter.',
                },
                {
                  title: 'Proven engine underneath',
                  body: 'The business logic rides on the battle-tested ERPNext engine, unmodified. Simple on top, standard below.',
                },
              ]}
            />
          </Reveal>
        </div>
      </section>

      {/* ── Gallery rail ─────────────────────────────────────────────── */}
      <section aria-label="More screens" className="border-t border-white/10 py-24 md:py-32">
        <div className="px-6 md:px-12 lg:px-24">
          <div className="mx-auto max-w-[1400px]">
            <Reveal>
              <SectionHead accent={A} title="a tour through the system" />
            </Reveal>
          </div>
        </div>
        <Reveal delay={120}>
          <GalleryRail
            accent={A}
            className="mt-12"
            shots={[
              {
                src: `${IMG}/invoices.webp`,
                alt: 'DilaERP sales invoice list with IQD amounts and payment statuses.',
                caption: 'Sales invoices',
              },
              {
                src: `${IMG}/crm-deals.webp`,
                alt: 'DilaERP CRM deals pipeline with open opportunities.',
                caption: 'CRM deals',
              },
              {
                src: `${IMG}/hr.webp`,
                alt: 'DilaERP employee list with departments and designations.',
                caption: 'HR and payroll',
              },
              {
                src: `${IMG}/coa.webp`,
                alt: 'DilaERP chart of accounts tree.',
                caption: 'Chart of accounts',
              },
            ]}
          />
        </Reveal>
      </section>

      {/* ── Demo + cross-sell ────────────────────────────────────────── */}
      <div className="px-6 md:px-12 lg:px-24">
        <div className="mx-auto max-w-[1400px]">
          <DemoBand
            ghost="DilaERP"
            title="run the whole business in your language"
            sub="Books, stock, payroll and POS in Sorani, Arabic or English. We migrate your data and train your team, in Erbil or remote."
            primary={{ label: 'Book a demo', href: '/contact' }}
            secondary={{ label: 'Open live demo', href: product.liveUrl, external: true }}
            accent={A}
            note={`Live demo: ${product.domain} · request demo access`}
          />
          <NextProducts
            className="mb-24"
            items={products
              .filter((p) => p.slug !== 'dilaerp')
              .map((p) => ({ slug: p.slug, name: p.name, tagline: p.tagline, accent: p.accent }))}
          />
        </div>
      </div>
    </div>
  );
}
